/* eslint-disable max-statements */

import { UseFormReturnType } from '@mantine/form';
import { useNavigate } from 'react-router-dom';

import { Customer } from '@e-origin/shared';

import { EditCustomerType } from '@enums';
import { useCreateCustomer } from '@hooks/customer/useCreateCustomer';
import { useUpdateCustomer } from '@hooks/customer/useUpdateCustomer';

export const useCustomerHandlers = (form: UseFormReturnType<any>, customerId: string, isCreate: boolean, generalDeclarant: any, customerDetails: any) => {
  const navigate = useNavigate();

  const updateCustomerMutation = useUpdateCustomer();
  const createCustomerMutation = useCreateCustomer((newCustomerId: string) => navigate(`/customers/${newCustomerId}`));

  const { declarant: customerDetailsDeclarant } = customerDetails || {};

  const handleEditCustomer = async (customerData: Partial<Customer>) => {
    return await updateCustomerMutation.mutateAsync({ params: { id: customerId }, body: customerData });
  };

  const handleSubmitCustomer = async (type: EditCustomerType, childForm: UseFormReturnType<any>) => {
    if (isCreate) {
      childForm.resetDirty({});
      return await createCustomerMutation.mutateAsync({
        body: {
          declarant: { ...form.values.declarant, _id: generalDeclarant?._id },
          name: form.values.name,
          payments: form.values.payments,
          representative: form.values.representative,
          evaluationMethods: form.values.evaluationMethods,
        },
      });
    }

    switch (type) {
      case EditCustomerType.DECLARANT: {
        return handleEditCustomer({
          name: form.values.name,
          declarant: {
            ...childForm.values.declarant,
            _id: customerDetailsDeclarant?._id || generalDeclarant._id,
          },
        });
      }
      case EditCustomerType.REPRESENTATIVE: {
        return handleEditCustomer({
          name: form.values.name,
          representative: childForm.values.representative,
        });
      }
      case EditCustomerType.PAYMENTS: {
        return handleEditCustomer({
          name: form.values.name,
          payments: { ...customerDetails.payments, ...childForm.values.payments },
          personPayingTheCustomDuty: {
            ...customerDetails.personPayingTheCustomDuty,
            ...childForm.values.personPayingTheCustomDuty,
          },
        });
      }
      case EditCustomerType.EVALUATION_METHODS: {
        return handleEditCustomer({
          name: form.values.name,
          evaluationMethods: { ...customerDetails.evaluationMethods, ...childForm.values.evaluationMethods },
        });
      }
      case EditCustomerType.DOCUMENTS: {
        return handleEditCustomer({
          name: form.values.name,
          documents: { ...customerDetails.documents, ...childForm.values.documents },
          personProvidingGuarantee: {
            ...customerDetails.personProvidingGuarantee,
            ...childForm.values.personProvidingGuarantee,
          },
        });
      }
      case EditCustomerType.API_USER: {
        return handleEditCustomer({
          name: form.values.name,
          externalUsers: childForm.values.externalUsers,
        });
      }
      case EditCustomerType.TRANSIT_AUTH: {
        return handleEditCustomer({
          name: form.values.name,
          transitAuthorisations: childForm.values.transitAuthorisations,
        });
      }
      case EditCustomerType.GUARANTEES_TRANSIT: {
        return handleEditCustomer({
          name: form.values.name,
          transitGuarantees: childForm.values.transitGuarantees,
          requestForTransitGuarantee: childForm.values.requestForTransitGuarantee,
        });
      }
      default:
        return handleEditCustomer({ name: form.values.name });
    }
  };

  const handleUpdatePayments = (updatedPayments: Customer['payments']) => {
    if (isCreate && form.setFieldValue) {
      form.setFieldValue('payments', updatedPayments);
    }

    if (!isCreate) {
      updateCustomerMutation.mutate({ params: { id: customerId }, body: { payments: updatedPayments } });
    }
  };

  return {
    handleSubmitCustomer,
    handleUpdatePayments,
  };
};
